import {
  BaseUser,
  TaskAssignee,
  TaskManualStatus,
  TaskOwnerType,
  TaskState,
  taskStateSchema,
} from "@models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "../../components/Snackbar/useSnackbar";
import {
  baseUserTodosSummaryQueryKey,
  getDocumentTemplatesQueryKey,
  getDocumentsQueryKey,
  getOrganizationUsersQueryKey,
  getOrgTasksQueryKey,
  getTaskStateActivityQueryKey,
  getTaskStatesQueryKey,
} from "../../config";
import {
  attachFileDocumentToTaskState,
  attachTaskStateDocument,
  createTaskState,
  deleteAdHocTask,
  markTrainingTaskRead,
  removeFileFromTaskState,
  signTrainingTask,
  UnifiedTask,
  updateAdHocTask,
  UpdateAdHocTaskData,
  updateTaskState,
} from "../../services";
import { invalidateCachedDashboardQueries } from "./dashboard-invalidation";

export type CreateTaskStateData = {
  taskKey: string;
  assigneeId?: string | null;
  dueDate?: string | null;
  manualStatus?: TaskManualStatus;
};

export type UpdateTaskStateData = {
  assigneeId?: string | null;
  dueDate?: string | null;
  manualStatus?: TaskManualStatus;
};

type TaskMutationContext = {
  previousTaskStates?: TaskState[];
  previousTasksAll?: UnifiedTask[];
  previousTasksMine?: UnifiedTask[];
  didOptimisticAssignee?: boolean;
  didOptimisticManualStatus?: boolean;
};

const getOptimisticAssignee = (
  users: BaseUser[] | undefined,
  assigneeId: string | null
): TaskAssignee | null => {
  if (!assigneeId) return null;
  const matchedUser = users?.find((user) => user.id === assigneeId);
  if (!matchedUser) return null;
  return {
    id: matchedUser.id,
    email: matchedUser.email,
    firstName: matchedUser.firstName ?? "",
    lastName: matchedUser.lastName ?? "",
  };
};

const updateTaskStatesAssignee = (
  taskStates: TaskState[] | undefined,
  {
    stateId,
    taskKey,
    assigneeId,
    assignee,
    orgId,
    deviceId,
  }: {
    stateId?: string;
    taskKey?: string | null;
    assigneeId: string | null;
    assignee: TaskAssignee | null;
    orgId: string;
    deviceId: string;
  }
): TaskState[] | undefined => {
  if (!taskStates) return taskStates;

  const existingIndex = taskStates.findIndex((state) =>
    stateId ? state.id === stateId : state.taskKey === taskKey
  );

  if (existingIndex >= 0) {
    return taskStates.map((state) =>
      state.id === taskStates[existingIndex].id
        ? {
          ...state,
          assigneeId,
          assignee,
          updatedAt: new Date(),
        }
        : state
    );
  }

  if (!taskKey) return taskStates;

  const newState: TaskState = {
    id: `temp-${taskKey}-${Date.now()}`,
    taskKey,
    ownerType: deviceId ? TaskOwnerType.DEVICE : TaskOwnerType.ORGANIZATION,
    ownerId: deviceId || orgId,
    organizationId: orgId,
    deviceId: deviceId || null,
    assigneeId,
    assignee,
    dueDate: null,
    manualStatus: TaskManualStatus.TODO,
    notesDocumentId: null,
    notesDocument: null,
    fileDocuments: [],
    createdBy: "temp",
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  return [...taskStates, newState];
};

const updateOrgTasksAssignee = (
  tasks: UnifiedTask[] | undefined,
  {
    stateId,
    taskKey,
    assignee,
  }: {
    stateId?: string;
    taskKey?: string | null;
    assignee: TaskAssignee | null;
  }
): UnifiedTask[] | undefined => {
  if (!tasks) return tasks;
  return tasks.map((task) => {
    const isMatch =
      (stateId && task.id === stateId) || (taskKey && task.taskKey === taskKey);
    if (!isMatch) return task;
    return {
      ...task,
      assignee: assignee
        ? {
          id: assignee.id,
          email: assignee.email,
          firstName: assignee.firstName,
          lastName: assignee.lastName,
        }
        : null,
    };
  });
};

const updateTaskStatesManualStatus = (
  taskStates: TaskState[] | undefined,
  {
    stateId,
    taskKey,
    manualStatus,
    orgId,
    deviceId,
  }: {
    stateId?: string;
    taskKey?: string | null;
    manualStatus: TaskManualStatus;
    orgId: string;
    deviceId: string;
  }
): TaskState[] | undefined => {
  if (!taskStates) return taskStates;

  const existingIndex = taskStates.findIndex((state) =>
    stateId ? state.id === stateId : state.taskKey === taskKey
  );

  if (existingIndex >= 0) {
    return taskStates.map((state) =>
      state.id === taskStates[existingIndex].id
        ? {
          ...state,
          manualStatus,
          updatedAt: new Date(),
        }
        : state
    );
  }

  if (!taskKey) return taskStates;

  const newState: TaskState = {
    id: `temp-${taskKey}-${Date.now()}`,
    taskKey,
    ownerType: deviceId ? TaskOwnerType.DEVICE : TaskOwnerType.ORGANIZATION,
    ownerId: deviceId || orgId,
    organizationId: orgId,
    deviceId: deviceId || null,
    assigneeId: null,
    assignee: null,
    dueDate: null,
    manualStatus,
    notesDocumentId: null,
    notesDocument: null,
    fileDocuments: [],
    createdBy: "temp",
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  return [...taskStates, newState];
};

const updateOrgTasksManualStatus = (
  tasks: UnifiedTask[] | undefined,
  {
    stateId,
    taskKey,
    manualStatus,
  }: {
    stateId?: string;
    taskKey?: string | null;
    manualStatus: TaskManualStatus;
  }
): UnifiedTask[] | undefined => {
  if (!tasks) return tasks;
  return tasks.map((task) => {
    const isMatch =
      (stateId && task.id === stateId) || (taskKey && task.taskKey === taskKey);
    if (!isMatch) return task;
    return {
      ...task,
      status: manualStatus,
    };
  });
};

export const useCreateTaskState = ({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (data: CreateTaskStateData) => {
      const { data: responseData } = await createTaskState(
        orgId,
        deviceId,
        data
      );
      return await taskStateSchema.validate(responseData);
    },
    onMutate: async (data): Promise<TaskMutationContext> => {
      if (data.assigneeId === undefined) {
        return {};
      }

      await queryClient.cancelQueries({
        queryKey: getTaskStatesQueryKey(orgId, deviceId),
      });
      await queryClient.cancelQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      await queryClient.cancelQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });

      const previousTaskStates = queryClient.getQueryData<TaskState[]>(
        getTaskStatesQueryKey(orgId, deviceId)
      );
      const previousTasksAll = queryClient.getQueryData<UnifiedTask[]>(
        getOrgTasksQueryKey(orgId, "all")
      );
      const previousTasksMine = queryClient.getQueryData<UnifiedTask[]>(
        getOrgTasksQueryKey(orgId, "mine")
      );

      const orgUsers = queryClient.getQueryData<BaseUser[]>(
        getOrganizationUsersQueryKey({ orgId })
      );
      const optimisticAssignee = getOptimisticAssignee(
        orgUsers,
        data.assigneeId ?? null
      );

      const updatedTaskStates = updateTaskStatesAssignee(previousTaskStates, {
        taskKey: data.taskKey,
        assigneeId: data.assigneeId ?? null,
        assignee: optimisticAssignee,
        orgId,
        deviceId,
      });

      const updatedTasksAll = updateOrgTasksAssignee(previousTasksAll, {
        taskKey: data.taskKey,
        assignee: optimisticAssignee,
      });
      const updatedTasksMine = updateOrgTasksAssignee(previousTasksMine, {
        taskKey: data.taskKey,
        assignee: optimisticAssignee,
      });

      if (updatedTaskStates) {
        queryClient.setQueryData(
          getTaskStatesQueryKey(orgId, deviceId),
          updatedTaskStates
        );
      }
      if (updatedTasksAll) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "all"),
          updatedTasksAll
        );
      }
      if (updatedTasksMine) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "mine"),
          updatedTasksMine
        );
      }

      return {
        previousTaskStates,
        previousTasksAll,
        previousTasksMine,
        didOptimisticAssignee: true,
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTaskStates) {
        queryClient.setQueryData(
          getTaskStatesQueryKey(orgId, deviceId),
          context.previousTaskStates
        );
      }
      if (context?.previousTasksAll) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "all"),
          context.previousTasksAll
        );
      }
      if (context?.previousTasksMine) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "mine"),
          context.previousTasksMine
        );
      }
      if (context?.didOptimisticAssignee) {
        showSnackbar("Failed to assign task. Please try again.");
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: getTaskStateActivityQueryKey(orgId, deviceId, data.id),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: baseUserTodosSummaryQueryKey,
      });
      invalidateCachedDashboardQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: getTaskStatesQueryKey(orgId, deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });
    },
  });
};

export const useUpdateTaskState = ({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      stateId,
      data,
    }: {
      stateId: string;
      data: UpdateTaskStateData;
    }) => {
      const { data: responseData } = await updateTaskState(
        orgId,
        deviceId,
        stateId,
        data
      );
      return await taskStateSchema.validate(responseData);
    },
    onMutate: async ({
      stateId,
      data,
    }): Promise<TaskMutationContext> => {
      const hasAssigneeChange = data.assigneeId !== undefined;
      const hasManualStatusChange = data.manualStatus !== undefined;

      if (!hasAssigneeChange && !hasManualStatusChange) {
        return {};
      }

      await queryClient.cancelQueries({
        queryKey: getTaskStatesQueryKey(orgId, deviceId),
      });
      await queryClient.cancelQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      await queryClient.cancelQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });

      const previousTaskStates = queryClient.getQueryData<TaskState[]>(
        getTaskStatesQueryKey(orgId, deviceId)
      );
      const previousTasksAll = queryClient.getQueryData<UnifiedTask[]>(
        getOrgTasksQueryKey(orgId, "all")
      );
      const previousTasksMine = queryClient.getQueryData<UnifiedTask[]>(
        getOrgTasksQueryKey(orgId, "mine")
      );

      const taskKeyFromState = previousTaskStates?.find(
        (state) => state.id === stateId
      )?.taskKey;

      let updatedTaskStates = previousTaskStates;
      let updatedTasksAll = previousTasksAll;
      let updatedTasksMine = previousTasksMine;
      let didOptimisticAssignee = false;
      let didOptimisticManualStatus = false;

      if (hasAssigneeChange) {
        const orgUsers = queryClient.getQueryData<BaseUser[]>(
          getOrganizationUsersQueryKey({ orgId })
        );
        const optimisticAssignee = getOptimisticAssignee(
          orgUsers,
          data.assigneeId ?? null
        );

        updatedTaskStates = updateTaskStatesAssignee(updatedTaskStates, {
          stateId,
          taskKey: taskKeyFromState,
          assigneeId: data.assigneeId ?? null,
          assignee: optimisticAssignee,
          orgId,
          deviceId,
        });

        updatedTasksAll = updateOrgTasksAssignee(updatedTasksAll, {
          stateId,
          taskKey: taskKeyFromState,
          assignee: optimisticAssignee,
        });
        updatedTasksMine = updateOrgTasksAssignee(updatedTasksMine, {
          stateId,
          taskKey: taskKeyFromState,
          assignee: optimisticAssignee,
        });
        didOptimisticAssignee = true;
      }

      if (hasManualStatusChange && data.manualStatus) {
        updatedTaskStates = updateTaskStatesManualStatus(updatedTaskStates, {
          stateId,
          taskKey: taskKeyFromState,
          manualStatus: data.manualStatus,
          orgId,
          deviceId,
        });

        updatedTasksAll = updateOrgTasksManualStatus(updatedTasksAll, {
          stateId,
          taskKey: taskKeyFromState,
          manualStatus: data.manualStatus,
        });
        updatedTasksMine = updateOrgTasksManualStatus(updatedTasksMine, {
          stateId,
          taskKey: taskKeyFromState,
          manualStatus: data.manualStatus,
        });
        didOptimisticManualStatus = true;
      }

      if (updatedTaskStates) {
        queryClient.setQueryData(
          getTaskStatesQueryKey(orgId, deviceId),
          updatedTaskStates
        );
      }
      if (updatedTasksAll) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "all"),
          updatedTasksAll
        );
      }
      if (updatedTasksMine) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "mine"),
          updatedTasksMine
        );
      }

      return {
        previousTaskStates,
        previousTasksAll,
        previousTasksMine,
        didOptimisticAssignee,
        didOptimisticManualStatus,
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTaskStates) {
        queryClient.setQueryData(
          getTaskStatesQueryKey(orgId, deviceId),
          context.previousTaskStates
        );
      }
      if (context?.previousTasksAll) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "all"),
          context.previousTasksAll
        );
      }
      if (context?.previousTasksMine) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "mine"),
          context.previousTasksMine
        );
      }
      if (context?.didOptimisticAssignee) {
        showSnackbar("Failed to assign task. Please try again.");
      }
      if (context?.didOptimisticManualStatus) {
        showSnackbar("Failed to update status. Please try again.");
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: baseUserTodosSummaryQueryKey,
      });
      invalidateCachedDashboardQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: getTaskStatesQueryKey(orgId, deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });
      if (variables?.stateId) {
        queryClient.invalidateQueries({
          queryKey: getTaskStateActivityQueryKey(
            orgId,
            deviceId,
            variables.stateId
          ),
        });
      }
    },
  });
};

export const useAttachFileDocumentToTaskState = ({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      stateId,
      fileDocumentId,
    }: {
      stateId: string;
      fileDocumentId: string;
    }) => {
      const { data: responseData } = await attachFileDocumentToTaskState(
        orgId,
        deviceId,
        stateId,
        fileDocumentId
      );
      return await taskStateSchema.validate(responseData);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: baseUserTodosSummaryQueryKey,
      });
      invalidateCachedDashboardQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: getDocumentTemplatesQueryKey(orgId, deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getTaskStatesQueryKey(orgId, deviceId),
      });
      // Also invalidate org tasks queries since TaskDetail uses useGetOrgTasks
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });
      if (variables?.stateId) {
        queryClient.invalidateQueries({
          queryKey: getTaskStateActivityQueryKey(
            orgId,
            deviceId,
            variables.stateId
          ),
        });
      }
    },
  });
};

export const useAttachTaskStateDocument = ({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      stateId,
      documentId,
    }: {
      stateId: string;
      documentId: string;
    }) => {
      const { data: responseData } = await attachTaskStateDocument(
        orgId,
        deviceId,
        stateId,
        { documentId }
      );
      return await taskStateSchema.validate(responseData);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: baseUserTodosSummaryQueryKey,
      });
      invalidateCachedDashboardQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: getDocumentTemplatesQueryKey(orgId, deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getTaskStatesQueryKey(orgId, deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });
      if (variables?.stateId) {
        queryClient.invalidateQueries({
          queryKey: getTaskStateActivityQueryKey(
            orgId,
            deviceId,
            variables.stateId
          ),
        });
      }
    },
  });
};

export const useRemoveFileFromTaskState = ({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      stateId,
      fileDocumentId,
    }: {
      stateId: string;
      fileDocumentId: string;
    }) => {
      const { data: responseData } = await removeFileFromTaskState(
        orgId,
        deviceId,
        stateId,
        fileDocumentId
      );
      return await taskStateSchema.validate(responseData);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: baseUserTodosSummaryQueryKey,
      });
      invalidateCachedDashboardQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: getDocumentTemplatesQueryKey(orgId, deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getDocumentsQueryKey(deviceId),
      });
      queryClient.invalidateQueries({
        queryKey: getTaskStatesQueryKey(orgId, deviceId),
      });
      // Also invalidate org tasks queries since TaskDetail uses useGetOrgTasks
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });
      if (variables?.stateId) {
        queryClient.invalidateQueries({
          queryKey: getTaskStateActivityQueryKey(
            orgId,
            deviceId,
            variables.stateId
          ),
        });
      }
    },
  });
};

export const useDeleteAdHocTask = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskKey }: { taskKey: string }) => {
      await deleteAdHocTask(orgId, taskKey);
    },
    onMutate: async ({ taskKey }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      await queryClient.cancelQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });

      // Snapshot the previous value
      const previousTasksAll = queryClient.getQueryData<UnifiedTask[]>(
        getOrgTasksQueryKey(orgId, "all")
      );
      const previousTasksMine = queryClient.getQueryData<UnifiedTask[]>(
        getOrgTasksQueryKey(orgId, "mine")
      );

      // Optimistically remove the task from cache
      if (previousTasksAll) {
        queryClient.setQueryData<UnifiedTask[]>(
          getOrgTasksQueryKey(orgId, "all"),
          previousTasksAll.filter((t) => t.taskKey !== taskKey)
        );
      }
      if (previousTasksMine) {
        queryClient.setQueryData<UnifiedTask[]>(
          getOrgTasksQueryKey(orgId, "mine"),
          previousTasksMine.filter((t) => t.taskKey !== taskKey)
        );
      }

      return { previousTasksAll, previousTasksMine };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousTasksAll) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "all"),
          context.previousTasksAll
        );
      }
      if (context?.previousTasksMine) {
        queryClient.setQueryData(
          getOrgTasksQueryKey(orgId, "mine"),
          context.previousTasksMine
        );
      }
    },
    onSettled: () => {
      // Invalidate to refetch
      queryClient.invalidateQueries({
        queryKey: baseUserTodosSummaryQueryKey,
      });
      invalidateCachedDashboardQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });
    },
  });
};

export const useUpdateAdHocTask = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskKey,
      data,
    }: {
      taskKey: string;
      data: UpdateAdHocTaskData;
    }) => {
      await updateAdHocTask(orgId, taskKey, data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: baseUserTodosSummaryQueryKey,
      });
      invalidateCachedDashboardQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });
    },
  });
};

const invalidateTaskCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  stateId: string
) => {
  queryClient.invalidateQueries({ queryKey: baseUserTodosSummaryQueryKey });
  queryClient.invalidateQueries({
    queryKey: getOrgTasksQueryKey(orgId, "all"),
  });
  queryClient.invalidateQueries({
    queryKey: getOrgTasksQueryKey(orgId, "mine"),
  });
  // Training hooks don't know deviceId, so match only the activity query
  // for this specific stateId (key shape: ["taskStateActivity", orgId, deviceId, stateId]).
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === "taskStateActivity" &&
        key[1] === orgId &&
        key[3] === stateId
      );
    },
  });
};

export const useMarkTrainingTaskRead = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ stateId }: { stateId: string }) => {
      const { data } = await markTrainingTaskRead(orgId, stateId);
      return data;
    },
    onError: (e) => {
      showSnackbar(
        (e as Error)?.message ?? "Could not mark training as read"
      );
    },
    onSettled: (_data, _error, variables) => {
      invalidateTaskCaches(queryClient, orgId, variables.stateId);
    },
  });
};

export const useSignTrainingTask = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      stateId,
      pin,
    }: {
      stateId: string;
      pin: string;
    }) => {
      const { data } = await signTrainingTask(orgId, stateId, pin);
      return data;
    },
    onError: (e) => {
      showSnackbar((e as Error)?.message ?? "Could not sign training");
    },
    onSettled: (_data, _error, variables) => {
      invalidateTaskCaches(queryClient, orgId, variables.stateId);
    },
  });
};
